import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Input, Label, CardHeader, CardBody, Badge, Alert } from 'reactstrap';
import Select from 'react-select';
import { Service } from './Service';
import axios from 'axios';

const ACTIVE_CUSTOM_STORAGE_KEY = 'microprod_active_custom_production';

const CustomProduction = () => {
    const service = React.useMemo(() => new Service(), []);
    const [ingredients, setIngredients] = useState([]);
    const [recipies, setRecipies] = useState([]);
    const [selectedRecipeId, setSelectedRecipeId] = useState('');
    const [targetQty, setTargetQty] = useState(1000);
    const [qtys, setQtys] = useState([]);
    const [step, setStep] = useState(0);
    const [weight, setWeight] = useState(0);
    const [actualQtys, setActualQtys] = useState([]);
    const [executing, setExecuting] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scaleConnected, setScaleConnected] = useState(true);
    const [resumedNotice, setResumedNotice] = useState(null);

    const saveCustomRunToStorage = (recId, tQty, currentQtys, currentStep, currentActuals, isExec) => {
        if (!isExec) return;
        try {
            const state = {
                selectedRecipeId: recId,
                targetQty: tQty,
                qtys: currentQtys || [],
                step: currentStep || 0,
                actualQtys: currentActuals || [],
                executing: true,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem(ACTIVE_CUSTOM_STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Error saving custom production to localStorage:', e);
        }
    };

    const clearCustomRunStorage = () => {
        try {
            localStorage.removeItem(ACTIVE_CUSTOM_STORAGE_KEY);
        } catch (e) {
            console.error('Error clearing custom production from localStorage:', e);
        }
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [ings, recs] = await Promise.all([
                service.getIngredients(),
                service.getRecipies()
            ]);
            setIngredients(ings || []);
            setRecipies(recs || []);

            // Check if there is an in-progress custom production saved
            let savedState = null;
            try {
                const rawSaved = localStorage.getItem(ACTIVE_CUSTOM_STORAGE_KEY);
                if (rawSaved) savedState = JSON.parse(rawSaved);
            } catch (e) {
                console.error('Error reading custom production from localStorage:', e);
            }

            if (savedState && savedState.executing && savedState.selectedRecipeId) {
                const matchedRecipe = (recs || []).find(r => r.id === savedState.selectedRecipeId);
                if (matchedRecipe) {
                    setSelectedRecipeId(savedState.selectedRecipeId);
                    setTargetQty(savedState.targetQty || matchedRecipe.qty || 1000);
                    setQtys(savedState.qtys || []);
                    setStep(savedState.step || 0);
                    setActualQtys(savedState.actualQtys || []);
                    setExecuting(true);
                    setCompleted(false);
                    setResumedNotice(`Ανακτήθηκε αυτόματα η custom παραγωγή για τη συνταγή "${matchedRecipe.title}" (Βήμα ${(savedState.step || 0) + 1} από ${(savedState.qtys || []).length}).`);
                    return;
                }
            }

            if (recs && recs.length > 0 && !selectedRecipeId) {
                setSelectedRecipeId(recs[0].id);
                setTargetQty(recs[0].qty || 1000);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, [service, selectedRecipeId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        let interval = null;
        if (executing) {
            interval = setInterval(() => {
                axios.get('http://localhost:3001')
                    .then(r => {
                        setScaleConnected(true);
                        if (r.data && (r.data.weight || r.data.weight === 0)) {
                            setWeight(Number(r.data.weight) || 0);
                        }
                    })
                    .catch(() => setScaleConnected(false));
            }, 800);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [executing]);

    const setZero = () => {
        axios.post('http://localhost:3001/zero?' + new Date().getTime(), { dateTime: new Date().getTime() }, { cache: false })
            .catch(e => console.error('Tare error:', e));
    };

    const selectedRecipe = recipies.find(r => r.id === selectedRecipeId);

    const startCustomRun = () => {
        if (!selectedRecipe || !targetQty || targetQty <= 0) return;
        const analogy = targetQty / (selectedRecipe.qty || 1);
        const scaled = (selectedRecipe.ingredients || []).filter(Boolean).map(ing => ({
            ...ing,
            qty: Math.round(ing.qty * analogy)
        }));
        setQtys(scaled);
        setStep(0);
        setActualQtys([]);
        setExecuting(true);
        setCompleted(false);
        saveCustomRunToStorage(selectedRecipeId, targetQty, scaled, 0, [], true);
    };

    const pauseAndExitCustom = () => {
        saveCustomRunToStorage(selectedRecipeId, targetQty, qtys, step, actualQtys, true);
        setExecuting(false);
    };

    const cancelAndResetCustom = () => {
        const confirmReset = window.confirm("Είστε βέβαιοι ότι θέλετε να ακυρώσετε την τρέχουσα custom παραγωγή;");
        if (confirmReset) {
            clearCustomRunStorage();
            setExecuting(false);
            setCompleted(false);
            setStep(0);
            setActualQtys([]);
            setQtys([]);
            setResumedNotice(null);
            loadData();
        }
    };

    const currentTarget = qtys[step] ? qtys[step].qty : 0;
    const isStepValid = () => {
        if (!qtys[step] || currentTarget <= 0) return false;
        const tolerance = currentTarget < 4000 ? 0.015 : 0.025;
        return weight >= currentTarget * (1 - tolerance) && weight <= currentTarget * (1 + tolerance);
    };

    const nextStep = () => {
        if (isStepValid()) {
            const nextActuals = [...actualQtys, { ...qtys[step], qty: weight }];
            setActualQtys(nextActuals);
            if (step < qtys.length - 1) {
                const nextStepIndex = step + 1;
                setStep(nextStepIndex);
                saveCustomRunToStorage(selectedRecipeId, targetQty, qtys, nextStepIndex, nextActuals, true);
            } else {
                setExecuting(false);
                setCompleted(true);
                clearCustomRunStorage();
            }
            setZero();
        } else {
            window.alert('Το βάρος δεν βρίσκεται εντός των ορίων της συνταγής.');
        }
    };

    const currentIng = qtys[step] ? ingredients.find(i => i.id === qtys[step].ingredientId) : null;

    return (
        <div>
            {resumedNotice && (
                <Alert color="info" className="d-flex align-items-center justify-content-between mb-4 shadow-sm" toggle={() => setResumedNotice(null)}>
                    <div>
                        <i className="bi bi-info-circle-fill me-2 fs-5"></i>
                        <span>{resumedNotice}</span>
                    </div>
                </Alert>
            )}

            {!executing && !completed && (
                <div className="ek-card">
                    <CardHeader className="ek-card-header">
                        <h5>
                            <i className="bi bi-gear-wide-connected text-primary"></i> Παραμετρική / Custom Παραγωγή
                        </h5>
                    </CardHeader>
                    <CardBody className="ek-card-body">
                        <p className="text-muted">
                            Επιλέξτε μία συνταγή και ορίστε την επιθυμητή τελική ποσότητα παραγωγής. Τα συστατικά θα κλιμακωθούν αυτόματα.
                        </p>

                        <Row className="mb-4">
                            <Col md="6">
                                <div className="ek-form-group mb-3">
                                    <Label className="ek-label">
                                        <i className="bi bi-journal-bookmark"></i> Επιλογή Συνταγής
                                    </Label>
                                    <Select
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        value={selectedRecipe ? { value: selectedRecipe.id, label: `${selectedRecipe.title} (Βάση: ${selectedRecipe.qty} gr)` } : null}
                                        onChange={(opt) => {
                                            if (opt) {
                                                setSelectedRecipeId(opt.value);
                                                const r = recipies.find(rc => rc.id === opt.value);
                                                if (r) setTargetQty(r.qty || 1000);
                                            }
                                        }}
                                        options={recipies.map(r => ({ value: r.id, label: `${r.title} (Βάση: ${r.qty} gr)` }))}
                                        placeholder="Επιλέξτε Συνταγή..."
                                    />
                                </div>
                            </Col>

                            <Col md="6">
                                <div className="ek-form-group mb-3">
                                    <Label className="ek-label">
                                        <i className="bi bi-speedometer2"></i> Επιθυμητή Ποσότητα Παραγωγής (gr)
                                    </Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={targetQty}
                                        onChange={(e) => setTargetQty(e.target.valueAsNumber || 0)}
                                    />
                                </div>
                            </Col>
                        </Row>

                        {selectedRecipe && (
                            <div className="p-3 bg-light rounded-3 border mb-4">
                                <h6 className="fw-bold text-dark mb-2">
                                    <i className="bi bi-list-check text-primary me-2"></i> Προεπισκόπηση Συστατικών (Κλίμακα {(targetQty / (selectedRecipe.qty || 1)).toFixed(2)}x)
                                </h6>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>Συστατικό</th>
                                                <th className="text-end">Αρχικό (gr)</th>
                                                <th className="text-end">Κλιμακωμένο (gr)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(selectedRecipe.ingredients || []).filter(Boolean).map((ing, idx) => {
                                                const matched = ingredients.find(i => i.id === ing.ingredientId);
                                                const scaledQty = Math.round(ing.qty * (targetQty / (selectedRecipe.qty || 1)));
                                                return (
                                                    <tr key={idx}>
                                                        <td>{matched ? matched.title : 'Συστατικό'}</td>
                                                        <td className="text-end text-muted">{ing.qty} gr</td>
                                                        <td className="text-end fw-bold text-primary">{scaledQty} gr</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-end">
                            <Button color="primary" size="lg" onClick={startCustomRun} disabled={loading || !selectedRecipe}>
                                <i className="bi bi-play-circle-fill"></i> Έναρξη Custom Παραγωγής
                            </Button>
                        </div>
                    </CardBody>
                </div>
            )}

            {executing && (
                <Row className="g-4">
                    <Col lg="8">
                        <div className="scale-monitor-card">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`ek-status-dot ${scaleConnected ? '' : 'bg-danger'}`}></span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Ζυγαριά Custom Παραγωγής</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Badge color="light" className="text-dark small border">
                                        <i className="bi bi-cloud-check text-success me-1"></i> Αυτόματη Αποθήκευση
                                    </Badge>
                                    <Button color="warning" size="sm" onClick={setZero} className="fw-bold">
                                        <i className="bi bi-arrow-counterclockwise"></i> Μηδενισμός
                                    </Button>
                                </div>
                            </div>

                            <div className="scale-target-box mb-3">
                                <span style={{ color: '#aae0ff', fontSize: '0.85rem' }}>
                                    Βήμα {step + 1} / {qtys.length}
                                </span>
                                <h2 style={{ fontSize: '1.85rem', fontWeight: 800, margin: 0, color: '#ffffff' }}>
                                    {currentIng ? currentIng.title : 'Συστατικό'}
                                </h2>
                                <div className="mt-2 text-info fs-5 fw-bold">
                                    Στόχος: {currentTarget} gr
                                </div>
                            </div>

                            <div className="text-center py-2">
                                <div className="scale-digit-display">
                                    {weight.toFixed(1)} <span className="scale-digit-unit">gr</span>
                                </div>
                            </div>

                            <div className="d-flex gap-3 justify-content-center pt-3">
                                <Button 
                                    color={isStepValid() ? "success" : "primary"} 
                                    size="lg" 
                                    onClick={nextStep}
                                    disabled={!isStepValid()}
                                >
                                    <i className="bi bi-check-circle-fill"></i> {step === qtys.length - 1 ? 'Ολοκλήρωση' : 'Επόμενο Υλικό'}
                                </Button>
                            </div>
                        </div>
                    </Col>

                    <Col lg="4">
                        <div className="ek-card">
                            <CardHeader className="ek-card-header">
                                <h5>Βήματα Συνταγής</h5>
                                <Badge color="primary">{step + 1} / {qtys.length}</Badge>
                            </CardHeader>
                            <CardBody className="ek-card-body p-3 scrollable-list">
                                {qtys.map((q, idx) => {
                                    const ing = ingredients.find(i => i.id === q.ingredientId);
                                    const isDone = idx < step;
                                    const isCur = idx === step;
                                    return (
                                        <div key={idx} className={`step-item-card ${isCur ? 'current' : ''} ${isDone ? 'completed' : ''}`}>
                                            <div>
                                                <div className="fw-semibold small">{ing ? ing.title : 'Συστατικό'}</div>
                                                <small className="text-muted">{q.qty} gr</small>
                                            </div>
                                            <Badge color={isDone ? "success" : (isCur ? "primary" : "secondary")}>
                                                {isDone ? "OK" : (isCur ? "Τρέχον" : "Αναμονή")}
                                            </Badge>
                                        </div>
                                    );
                                })}

                                <div className="mt-4 pt-3 border-top d-flex flex-column gap-2">
                                    <Button color="outline-primary" block size="sm" onClick={pauseAndExitCustom}>
                                        <i className="bi bi-pause-circle me-1"></i> Προσωρινή Παύση & Έξοδος
                                    </Button>
                                    <Button color="outline-danger" block size="sm" onClick={cancelAndResetCustom}>
                                        <i className="bi bi-trash me-1"></i> Ακύρωση & Επανεκκίνηση
                                    </Button>
                                </div>
                            </CardBody>
                        </div>
                    </Col>
                </Row>
            )}

            {completed && (
                <div className="ek-card p-5 text-center">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                    <h3 className="mt-3 text-dark fw-bold">Η Custom Παραγωγή Ολοκληρώθηκε!</h3>
                    <p className="text-muted">Όλα τα συστατικά της συνταγής {selectedRecipe?.title} ζυγίστηκαν με επιτυχία.</p>
                    <Button color="primary" onClick={() => { setCompleted(false); setExecuting(false); }}>
                        <i className="bi bi-arrow-left"></i> Νέα Custom Παραγωγή
                    </Button>
                </div>
            )}
        </div>
    );
};

export { CustomProduction };
export default CustomProduction;
